import { Grid, Typography } from '@mui/material';
import { LetterReceiptRow } from '@/components/common/LetterReceiptRow';
import { PaymentMethod } from '@/types/payment/PaymentMethod';
import { PaymentProvider } from '@/types/payment/PaymentProvider';
import { formatCurrency, getNumEnding } from '@/utils/string-utils';
import { getCommission, getCostWithCommission } from '@/utils/currency-utils';
import React, { FC } from 'react';

interface Props {
  countCharacters?: number;
  photoCount: number;
  sudokuAttached: boolean;
  sudokuCount?: number;
  crosswordAttached: boolean;
  crosswordCount?: number;
  newsAttached: boolean;
  sportsNewsAttached: boolean;
  pages?: number;
  letterCost?: number;
  authorizedLetterCost?: number;
  photoCost?: number;
  authorizedPhotoCost?: number;
  sudokuCost?: number;
  authorizedSudokuCost?: number;
  crosswordCost?: number;
  authorizedCrosswordCost?: number;
  newsCost?: number;
  authorizedNewsCost?: number;
  isAnswerOrdered: boolean;
  answersCount?: number;
  totalCost?: number;
  authorizedTotalCost?: number;
  paymentMethod: PaymentMethod;
  paymentProvider?: PaymentProvider;
  isInitiative?: boolean;
}

export const LetterReceipt: FC<Props> = (props: Props) => {
  const {
    countCharacters,
    photoCount,
    sudokuAttached,
    sudokuCount,
    crosswordAttached,
    crosswordCount,
    newsAttached,
    sportsNewsAttached,
    pages,
    letterCost,
    authorizedLetterCost,
    photoCost,
    authorizedPhotoCost,
    sudokuCost,
    authorizedSudokuCost,
    crosswordCost,
    authorizedCrosswordCost,
    newsCost,
    authorizedNewsCost,
    isAnswerOrdered,
    answersCount,
    totalCost,
    authorizedTotalCost,
    paymentMethod,
    paymentProvider,
    isInitiative,
  } = props;

  const costByMethod = (authorized?: number, regular?: number): number | undefined =>
    paymentMethod === PaymentMethod.BALANCE ? authorized : regular;

  const calculatedLetterCost: number | undefined = costByMethod(authorizedLetterCost, letterCost);
  const calculatedPhotoCost: number | undefined = costByMethod(authorizedPhotoCost, photoCost);
  const calculatedSudokuCost: number | undefined = costByMethod(authorizedSudokuCost, sudokuCost);
  const calculatedCrosswordCost: number | undefined = costByMethod(authorizedCrosswordCost, crosswordCost);
  const calculatedNewsCost: number | undefined = costByMethod(authorizedNewsCost, newsCost);
  const calculatedTotalCost: number | undefined = costByMethod(authorizedTotalCost, totalCost);

  return (
    <Grid container={true} direction={'column'} spacing={4}>
      {((countCharacters && countCharacters > 0) ||
        isInitiative ||
        (photoCount > 0 && calculatedPhotoCost) ||
        (sudokuAttached && calculatedSudokuCost) ||
        (crosswordAttached && calculatedCrosswordCost) ||
        (newsAttached && calculatedNewsCost) ||
        (sportsNewsAttached && calculatedNewsCost) ||
        ((paymentMethod === PaymentMethod.SBP || paymentMethod === PaymentMethod.ONLINE) &&
          (calculatedTotalCost || isInitiative))) && (
        <Grid item={true}>
          <Grid container={true} direction={'column'} spacing={1.5}>
            {countCharacters && countCharacters > 0 ? (
              <Grid item={true}>
                <LetterReceiptRow
                  label={`Письмо: ${countCharacters} ${getNumEnding(countCharacters || 0, [
                    'символ',
                    'символа',
                    'символов',
                  ])}, ${pages || '...'} ${getNumEnding(pages || 0, ['страница', 'страницы', 'страниц'])}`}
                  price={pages && calculatedLetterCost && formatCurrency(pages * calculatedLetterCost)}
                />
              </Grid>
            ) : (
              isInitiative && (
                <Grid item={true}>
                  <LetterReceiptRow
                    label={`Письмо: 1 страница`}
                    price={calculatedLetterCost && formatCurrency(calculatedLetterCost)}
                  />
                </Grid>
              )
            )}
            {photoCount > 0 && calculatedPhotoCost && (
              <Grid item={true}>
                <LetterReceiptRow
                  label={`Фото х${photoCount}`}
                  price={formatCurrency(calculatedPhotoCost * photoCount)}
                />
              </Grid>
            )}
            {sudokuAttached && calculatedSudokuCost && (
              <Grid item={true}>
                <LetterReceiptRow
                  label={`Судоку х${sudokuCount}`}
                  price={formatCurrency(calculatedSudokuCost * (sudokuCount ? sudokuCount : 1))}
                />
              </Grid>
            )}
            {crosswordAttached && calculatedCrosswordCost && (
              <Grid item={true}>
                <LetterReceiptRow
                  label={`Кроссворд х${crosswordCount}`}
                  price={formatCurrency(calculatedCrosswordCost * (crosswordCount ? crosswordCount : 1))}
                />
              </Grid>
            )}
            {newsAttached && calculatedNewsCost && (
              <Grid item={true}>
                <LetterReceiptRow label={'Новости'} price={formatCurrency(calculatedNewsCost)} />
              </Grid>
            )}
            {sportsNewsAttached && calculatedNewsCost && (
              <Grid item={true}>
                <LetterReceiptRow label={'Новости спорта'} price={formatCurrency(calculatedNewsCost)} />
              </Grid>
            )}
            {isAnswerOrdered && calculatedLetterCost && (
              <Grid item={true}>
                <LetterReceiptRow
                  label={`Бланк ответа х${answersCount}`}
                  price={formatCurrency(calculatedLetterCost * (answersCount ? answersCount : 1))}
                />
              </Grid>
            )}
            {(paymentMethod === PaymentMethod.SBP || paymentMethod === PaymentMethod.ONLINE) &&
              (calculatedTotalCost || isInitiative) && (
                <Grid item={true}>
                  <LetterReceiptRow
                    label={'Сервисный сбор'}
                    price={
                      isInitiative && calculatedLetterCost
                        ? formatCurrency(getCommission(calculatedLetterCost, paymentMethod, paymentProvider))
                        : calculatedTotalCost &&
                          formatCurrency(getCommission(calculatedTotalCost, paymentMethod, paymentProvider))
                    }
                  />
                </Grid>
              )}
          </Grid>
        </Grid>
      )}
      <Grid item={true}>
        <Grid container={true} justifyContent={'space-between'}>
          <Grid item={true}>
            <Typography variant={'body1'} fontWeight={500} fontSize={24}>
              {'Итого'}
            </Typography>
          </Grid>
          <Grid item={true}>
            <Typography variant={'body1'} fontWeight={500} fontSize={24}>
              {isInitiative && calculatedLetterCost
                ? formatCurrency(getCostWithCommission(calculatedLetterCost, paymentMethod, paymentProvider))
                : calculatedTotalCost &&
                  formatCurrency(getCostWithCommission(calculatedTotalCost, paymentMethod, paymentProvider))}
            </Typography>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};
